import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AutoLogin } from "./auth/AutoLogin";
import MainLayout from "./layouts/MainLayout";
import LandingPage from "./pages/LandingPage";
import DocTree from "./pages/DocTree";
import AdminSpecsPage from "./pages/AdminSpecsPage";
import AdminAclsPage from "./pages/AdminAclsPage";
import AdminApprovalPage from "./pages/AdminApprovalPage";
import MyApiKeys from "./pages/MyApiKeys";
import TelegramIntegrationPage from "./pages/TelegramIntegrationPage";
import SignatureClientPage from "./pages/SignatureClientPage";
import ApiKeyAdmin from "./pages/ApiKeyAdmin";
import AdminRetentionPoliciesPage from "./pages/AdminRetentionPoliciesPage";
import AdminStatsPage from "./pages/AdminStatsPage";
import AdminIndexPage from "./pages/AdminIndexPage";
import EmbeddedDocumentViewer from "./pages/EmbeddedDocumentViewer";
import BatchSignatureHarness from "./pages/BatchSignatureHarness";
import Login from "./components/Login";
import ErrorBoundary from "./components/ErrorBoundary";
import { NotificationProvider } from "./contexts/NotificationContext";
import NotificationContainer from "./components/NotificationContainer";
import ProtectedRoute from "./components/ProtectedRoute";
import { ConfirmProvider } from "./components/ui/ConfirmProvider";
import { ENV } from "./config/env";

const App = () => {
  // El título de la pestaña es white-label: index.html trae un placeholder neutro
  // y acá lo sobreescribimos con el nombre configurado (ENV.APP_NAME).
  useEffect(() => {
    if (ENV.APP_NAME) document.title = ENV.APP_NAME;
  }, []);

  // El AuthProvider NO va acá: vive en index.js, por encima del ErrorBoundary. Estuvo
  // montado en los dos lados a la vez, y cada AuthProvider instancia su propio UserManager
  // (react-oidc-context.js:150-152) — o sea dos SilentRenewService renovando la misma sesión
  // contra un refresh token que Azure AD rota y acepta una sola vez, y dos signinCallback
  // canjeando el mismo `?code=`. Lo cubre un test en App.test.jsx (#168).
  return (
      <NotificationProvider>
        <ConfirmProvider>
        <Router>
          <Routes>
            {/* Rutas embebibles - sin AutoLogin para evitar CSP restrictivo de Keycloak */}
            <Route path="/embed/document/:documentId" element={<EmbeddedDocumentViewer />} />
            <Route path="/embed/document" element={<EmbeddedDocumentViewer />} />

            {/* Harness de test E2E (Playwright) — SOLO dev/test, nunca en prod. Sin AutoLogin. */}
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/embed/batch-signature-harness" element={<BatchSignatureHarness />} />
            )}

            {/* Rutas principales - con AutoLogin */}
            <Route path="/*" element={
              <AutoLogin>
                <ErrorBoundary
                  fallbackTitle="Error en la navegación"
                  fallbackMessage="Ocurrió un error al cargar la página. Puedes intentar nuevamente o volver al inicio."
                >
                  <Routes>
                    <Route path="/" element={<MainLayout />}>
                      <Route index element={<LandingPage />} />
                      <Route path="doctree/*" element={
                        <ProtectedRoute requiredRole="VIEWER">
                          <DocTree />
                        </ProtectedRoute>
                      } />

                      {/* Rutas protegidas */}
                      <Route path="admin/specs" element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminSpecsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/acls" element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminAclsPage />
                        </ProtectedRoute>
                      } />
                      <Route path="quality-control" element={
                        <ProtectedRoute requiredRoles={['REVIEWER', 'APPROVER', 'SIGNER']}>
                          <AdminApprovalPage />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/api-keys" element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <ApiKeyAdmin />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/retention-policies" element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminRetentionPoliciesPage />
                        </ProtectedRoute>
                      } />
                      <Route path="api-keys" element={
                        <ProtectedRoute requiredRole="IMPORTER">
                          <MyApiKeys />
                        </ProtectedRoute>
                      } />
                      <Route path="integrations/telegram" element={
                        <ProtectedRoute requiredRole="IMPORTER">
                          <TelegramIntegrationPage />
                        </ProtectedRoute>
                      } />
                      <Route path="cliente-firma" element={
                        <ProtectedRoute requiredRole="SIGNER">
                          <SignatureClientPage />
                        </ProtectedRoute>
                      } />
                      <Route path="admin/index" element={
                        <ProtectedRoute requiredRole="ADMIN">
                          <AdminIndexPage />
                        </ProtectedRoute>
                      } />
                      {/* Estadísticas de series: cualquier usuario autenticado. Enumerar roles dejaba
                          afuera al Firmador, y como la pantalla de inicio SÍ ofrecía la tarjeta sin
                          condición, el firmante la veía, la clickeaba y ProtectedRoute lo devolvía al
                          inicio al instante: desde su lado, el botón no hacía nada (GitLab #33/#34).
                          Los datos que muestra ya los sirve el API a cualquier autenticado. */}
                      <Route path="admin/stats" element={
                        <ProtectedRoute>
                          <AdminStatsPage />
                        </ProtectedRoute>
                      } />
                    </Route>
                    <Route path="/login" element={<Login />} />
                  </Routes>
                </ErrorBoundary>
              </AutoLogin>
            } />
          </Routes>
        </Router>
        <NotificationContainer />
        </ConfirmProvider>
      </NotificationProvider>
  );
};

export default App;